/**
 * Main (shared) WebGLRenderer for all top-level scenes.
 *
 * iOS Safari cannot reliably reclaim WebGL context memory between
 * `new WebGLRenderer()` + `forceContextLoss()` cycles. Even with full
 * JS-side cleanup (dispose, scene.clear, store reset, worker terminate),
 * iOS WebKit accumulates WebGL driver-level state that's invisible to JS,
 * leading to OOM tab kills after 2-3 main-scene transitions.
 *
 * Solution: a single renderer instance shared by splat / top-level GLB
 * scenes. Scenes ACQUIRE the renderer on mount, RELEASE on
 * unmount. The WebGL context lives for the entire app session (no
 * construction, no `forceContextLoss` during transitions).
 *
 * PoiArtifact overlay keeps its OWN renderer via `poiArtifactHost.ts` —
 * this separation is intentional (artifact needs `antialias: true` for
 * crisp model rendering; main renderer uses `antialias: false` to stay
 * compatible with spark splat rendering).
 *
 * Invariant: at most 2 active WebGL contexts per tab:
 *   1. Main renderer (always alive after first acquire)
 *   2. PoiArtifact shell (optional, cached by poiArtifactHost)
 *
 * Contract:
 * - Per-scene render-target state (clearColor, pixelRatio, viewport size,
 *   tone mapping) MUST be set after acquire — the renderer does not know
 *   about the incoming scene's visual requirements.
 * - Per-scene CONTENT (textures, geometries, materials, managers) must
 *   still be disposed on scene teardown. The shared renderer does not
 *   free these automatically.
 * - The ONLY place `renderer.dispose()` / `forceContextLoss()` is called
 *   for main scenes is `disposeMainRendererForAppUnmount()` (rare).
 */

import * as THREE from 'three';
import { trackWebGlContextLost } from '../analytics/performanceTracker';
import {
  incrementWebGLContext,
  decrementWebGLContext,
  logEvent,
} from '../debug/memoryTracker';

let instance: THREE.WebGLRenderer | null = null;
let currentClaimant: string | null = null;

/**
 * Construct the shared renderer with a configuration compatible with all
 * main-scene types. Choices:
 * - `antialias: false` — splat (spark) does shader-based AA internally;
 *   MSAA would double-AA and hurt perf. Top-level GLB loses
 *   MSAA crispness as a trade-off.
 * - `alpha: true` — splat requires transparency for blend.
 * - `stencil: false, depth: true` — common denominator.
 * - `powerPreference: 'high-performance'` — prefer discrete GPU on mobile.
 */
const createRenderer = (): THREE.WebGLRenderer => {
  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    alpha: true,
    stencil: false,
    depth: true,
    powerPreference: 'high-performance',
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  trackWebGlContextLost(renderer.domElement);
  incrementWebGLContext('main');
  logEvent('mainRenderer: created');
  return renderer;
};

/**
 * Acquire the shared renderer for the given claimant and container.
 *
 * Moves the renderer's `<canvas>` into the provided container (appending
 * as the only child, removing from any previous parent). Resets internal
 * render-list state so no stale batch data from the previous scene bleeds
 * into the first frame of the next.
 *
 * @param claimantId stable id identifying the acquiring scene
 *   (e.g. `'splat-kaaba'`, `'glb-mina'`, `'globalMap'`). Used to enforce
 *   exclusive ownership and detect leaks.
 * @param container DOM element that will host the renderer's canvas.
 */
export const acquireMainRenderer = (
  claimantId: string,
  container: HTMLElement,
): THREE.WebGLRenderer => {
  if (!instance) {
    instance = createRenderer();
  }
  if (currentClaimant && currentClaimant !== claimantId) {
    // Previous claimant didn't release — indicates a logic bug in a caller.
    // We proceed anyway (canvas migrates to new container) but log loudly
    // so the bug is catchable in mem-debug logs.
    logEvent('mainRenderer: acquire WITHOUT release', {
      previousClaimant: currentClaimant,
      newClaimant: claimantId,
    });
  }
  currentClaimant = claimantId;

  const canvas = instance.domElement;
  if (canvas.parentElement !== container) {
    if (canvas.parentElement) canvas.parentElement.removeChild(canvas);
    canvas.style.position = 'absolute';
    // Use ONLY top/left, not `inset: 0`. With `renderer.setSize(...)`
    // setting an explicit pixel `style.width`/`style.height`, declaring
    // `right: 0` (via `inset: 0`) would over-constrain the box. Per CSS
    // spec, the containing-block direction then decides which side to
    // ignore: LTR drops `right`, RTL drops `left` → in an RTL document
    // canvas would anchor at container's physical right edge, leaving
    // canvas-pixel-0 at viewport-x = -8 while `.scene-poi-layer` (not
    // over-constrained, no explicit width) stays anchored at container
    // left. That 460-px mismatch desynced POI markers from scene
    // content in RTL. Setting only `top`/`left` leaves `right`/`bottom`
    // auto — width and height are governed exclusively by the explicit
    // pixel values from `setSize`, and the anchor is always the
    // physical top-left of the container regardless of direction.
    canvas.style.top = '0';
    canvas.style.left = '0';
    container.appendChild(canvas);
  }

  // Clear per-frame state left over from the previous scene's rendering.
  instance.renderLists.dispose();

  logEvent('mainRenderer: acquired', { claimantId });
  return instance;
};

/**
 * Release the shared renderer. Detaches the canvas from its current parent
 * and clears internal render-list state. Does NOT dispose the renderer or
 * lose the WebGL context — the renderer is reused by the next scene.
 *
 * Ownership is enforced: only the current claimant can release. Calls from
 * stale claimants are ignored (no-op + log).
 */
export const releaseMainRenderer = (claimantId: string): void => {
  if (!instance) return;
  if (currentClaimant !== claimantId) {
    logEvent('mainRenderer: release ignored (claimant mismatch)', {
      askedClaimant: claimantId,
      currentClaimant,
    });
    return;
  }
  const canvas = instance.domElement;
  if (canvas.parentElement) {
    canvas.parentElement.removeChild(canvas);
  }
  instance.renderLists.dispose();
  currentClaimant = null;
  logEvent('mainRenderer: released', { claimantId });
};

/**
 * Full teardown of the shared renderer. Only used on hard app-unmount
 * (e.g. React Strict Mode double-mount, hot reload). Normal scene
 * transitions MUST NOT call this.
 */
export const disposeMainRendererForAppUnmount = (): void => {
  if (!instance) return;
  logEvent('mainRenderer: full dispose (app unmount)');
  const canvas = instance.domElement;
  if (canvas.parentElement) canvas.parentElement.removeChild(canvas);
  instance.renderLists.dispose();
  instance.dispose();
  instance.forceContextLoss();
  decrementWebGLContext('main');
  instance = null;
  currentClaimant = null;
};

/** Diagnostic introspection for tests/debug overlay. */
export const getMainRendererClaimant = (): string | null => currentClaimant;
